import React, { useEffect, useState } from 'react';
import { LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';
import { useParams, useNavigate } from 'react-router-dom';
import { useMonitoringPortalContext } from 'hooks/useMonitoringPortalContext';
import { ArrowLeft, RefreshCw, X, Eye, Gamepad2, Copy, Check, Trophy, Hash, Activity, Receipt, Coins, Percent } from 'lucide-react';
import AdminHandCards from 'components/AdminHandCards';
import {
  BATTLE_TYPE,
  BATTLE_TYPE_LABEL,
  BATTLE_STATUS_LABEL,
  ROUND_STATUS_LABEL,
  roundActionLabel,
  transactionTypeLabel,
  labelFor,
  statusPillByLabel,
} from 'constants/enums';

// Pill styling from a numeric status code, resolved through the given label map.
const pill = (map: Record<number, string>, code: number | string) => statusPillByLabel(labelFor(map, code));

const playerLabel = (p: { display_name?: string | null; username?: string | null; user_id?: number }) =>
  p.display_name?.trim() || p.username?.trim() || (p.user_id != null ? `USR-${p.user_id}` : '—');

const externalUserId = (p: { external_user_id?: string | null; user_id?: number }) =>
  p.external_user_id?.trim() || (p.user_id != null ? `U${p.user_id}` : '—');

const handResultLabel = (result?: string | null) => {
  switch (result) {
    case 'winner':
      return 'Winner';
    case 'folded':
      return 'Folded';
    case 'active':
      return 'Active';
    default:
      return '—';
  }
};

const handResultPill = (result?: string | null) => {
  switch (result) {
    case 'winner':
      return 'bg-amber-500/15 text-amber-800 border-amber-200';
    case 'folded':
      return 'bg-subtle text-muted border-line';
    case 'active':
      return 'bg-indigo-500/10 text-indigo-700 border-indigo-100';
    default:
      return 'bg-subtle text-muted border-line';
  }
};
const money = (v: any) => parseFloat(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
const dateTime = (v: any) => (v ? new Date(v).toLocaleString() : '—');

function formatDuration(start?: string, end?: string): string | null {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-line/70 last:border-0 last:pb-0">
      <span className="text-xs font-bold text-muted uppercase tracking-wider shrink-0 pt-0.5">{label}</span>
      <span className="text-sm font-semibold text-heading text-right break-all">{children}</span>
    </div>
  );
}

function FinancialStatCard({
  label,
  icon: Icon,
  cardClass,
  iconWrapClass,
  labelClass,
  children,
}: {
  label: string;
  icon: React.ElementType;
  cardClass: string;
  iconWrapClass: string;
  labelClass: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${cardClass}`}>
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg mb-3 ${iconWrapClass}`}>
        <Icon size={18} strokeWidth={2} />
      </div>
      <p className={`text-[10px] font-bold uppercase tracking-wider ${labelClass}`}>{label}</p>
      <div className="mt-1.5 leading-tight">{children}</div>
    </div>
  );
}

function UuidCopyRow({ uuid }: { uuid?: string | null }) {
  const [copied, setCopied] = useState(false);
  const full = uuid?.trim() || '—';
  const canCopy = full !== '—';

  const copyId = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="flex items-start gap-2 rounded-xl border border-line bg-subtle/30 px-4 py-3">
      <Hash size={16} className="text-muted shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold text-muted uppercase tracking-wider mb-1">Battle ID</p>
        <p className="font-mono text-xs text-body break-all leading-relaxed">{full}</p>
      </div>
      {canCopy && (
        <button
          type="button"
          onClick={() => void copyId()}
          title={copied ? 'Copied' : 'Copy ID'}
          aria-label={copied ? 'Copied ID' : 'Copy ID'}
          className="shrink-0 p-2 rounded-lg border border-line bg-card text-muted hover:text-heading hover:bg-subtle transition-colors"
        >
          {copied ? <Check size={14} strokeWidth={2.5} className="text-emerald-600" /> : <Copy size={14} strokeWidth={2} />}
        </button>
      )}
    </div>
  );
}

const BattleDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const portal = useMonitoringPortalContext();
  const { apiClient } = portal;

  const [battleDetails, setBattleDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Single round drill-down (modal)
  const [selectedRound, setSelectedRound] = useState<any | null>(null);
  const [roundDetails, setRoundDetails] = useState<any | null>(null);
  const [roundLoading, setRoundLoading] = useState(false);

  const fetchDetails = () => {
    if (!id) return;
    setLoading(true);
    setNotFound(false);
    apiClient.get(portal.endpoints.battleDetails(id))
      .then((res) => {
        if (res.success && res.data) setBattleDetails(res.data);
        else setNotFound(true);
      })
      .catch((err) => { console.error('Failed to fetch battle details:', err); setNotFound(true); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDetails(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const openRound = (battleId: number, round: any) => {
    setSelectedRound(round);
    setRoundDetails(null);
    setRoundLoading(true);
    apiClient.get(portal.endpoints.roundDetails(battleId, round.id))
      .then((res) => { if (res.success && res.data) setRoundDetails(res.data); })
      .catch((err) => console.error('Failed to fetch round details:', err))
      .finally(() => setRoundLoading(false));
  };
  const closeRound = () => { setSelectedRound(null); setRoundDetails(null); };

  const openPlayer = (userId: number) => navigate(portal.playerDetailPath(userId));

  const battle = battleDetails?.battle;
  const handPlayers = battleDetails?.players ?? [];
  const totalBattleAmount = Math.max(0, parseFloat(battle?.total_battle_amount ?? 0));
  const commissionAmount = Math.max(0, parseFloat(battle?.commission_amount ?? 0));
  const winAmount = Math.max(0, parseFloat(battle?.win_amount ?? 0));
  const hasFinancials = totalBattleAmount > 0;
  const commissionIsEstimated = Boolean(battle?.commission_is_estimated);
  const commissionPct = Number(battle?.house_rake_percentage);
  const variantLabel = battle?.variant_name?.trim() || battle?.variant_code?.trim() || '—';
  const isCompleted = Boolean(battle?.ended_at);
  const sym = battle?.currency_symbol || '₹';

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="bg-card p-6 rounded-2xl border border-line flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(portal.backToBattlesPath)}
            className="p-2.5 rounded-xl bg-subtle hover:bg-subtle text-muted hover:text-heading border border-line transition-all"
            title="Back to battles">
            <ArrowLeft size={20} />
          </button>
          <div className="p-3 bg-indigo-500/10 text-indigo-600 rounded-xl"><Gamepad2 size={24} /></div>
          <div>
            <h3 className="text-xl font-bold text-heading">Battle BTL-{id}</h3>
            <p className="text-sm text-muted">One hand (deal → winner) · betting rounds &amp; action log</p>
          </div>
        </div>
        <button onClick={fetchDetails} className="p-2 text-muted hover:text-heading" title="Refresh">
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {loading ? (
        <div className="bg-card rounded-2xl shadow-sm border border-line p-16 text-center text-muted font-semibold flex items-center justify-center gap-2">
          <RefreshCw className="animate-spin" /> Loading battle snapshot…
        </div>
      ) : notFound || !battle ? (
        <div className="bg-card rounded-2xl shadow-sm border border-line p-16 text-center text-muted font-medium">
          Battle not found.
        </div>
      ) : (
        <>
          {/* Battle summary — two-part layout: details + timeline */}
          <div className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden">
            <div className="px-6 py-5 border-b border-line bg-gradient-to-r from-indigo-500/5 to-transparent">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h4 className="font-bold text-heading text-lg">Battle Summary</h4>
                  <p className="text-sm text-muted mt-0.5">
                    BTL-{battle.id}
                    {variantLabel !== '—' ? ` · ${variantLabel}` : ''}
                    {battle.table_name ? ` · ${battle.table_name}` : ''}
                  </p>
                </div>
                <span className={`self-start px-3 py-1 inline-flex text-xs font-bold rounded-full border ${pill(BATTLE_STATUS_LABEL, battle.status)}`}>
                  {labelFor(BATTLE_STATUS_LABEL, battle.status)}
                </span>
              </div>
            </div>

            <div className="px-6 pt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FinancialStatCard
                label="Total battle amount"
                icon={Coins}
                cardClass="bg-gradient-to-br from-sky-500/20 via-sky-500/10 to-cyan-500/5 border-sky-200/80"
                iconWrapClass="bg-sky-500/20 text-sky-700"
                labelClass="text-sky-800/80"
              >
                <span className="text-xl font-extrabold tabular-nums text-sky-900">
                  {hasFinancials ? `${sym}${money(totalBattleAmount)}` : '—'}
                </span>
              </FinancialStatCard>
              <FinancialStatCard
                label="Commission amount"
                icon={Percent}
                cardClass="bg-gradient-to-br from-amber-500/20 via-amber-500/10 to-yellow-500/5 border-amber-200/80"
                iconWrapClass="bg-amber-500/20 text-amber-700"
                labelClass="text-amber-800/80"
              >
                {hasFinancials ? (
                  <>
                    <span className={`text-xl font-extrabold tabular-nums ${commissionIsEstimated ? 'text-amber-600' : 'text-amber-800'}`}>
                      {sym}{money(commissionAmount)}
                      {commissionIsEstimated ? ' (est.)' : ''}
                    </span>
                    {Number.isFinite(commissionPct) && commissionPct >= 0 && (
                      <p className="text-xs font-semibold text-amber-700/80 mt-1 normal-case tracking-normal">
                        {commissionPct}% commission
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-sm font-bold text-emerald-700">In progress</span>
                    {Number.isFinite(commissionPct) && commissionPct >= 0 && (
                      <p className="text-xs font-semibold text-amber-700/80 mt-1 normal-case tracking-normal">
                        {commissionPct}% commission
                      </p>
                    )}
                  </>
                )}
              </FinancialStatCard>
              <FinancialStatCard
                label="Win amount"
                icon={Trophy}
                cardClass="bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-green-500/5 border-emerald-200/80"
                iconWrapClass="bg-emerald-500/20 text-emerald-700"
                labelClass="text-emerald-800/80"
              >
                {isCompleted && battle.winner_user_id && winAmount > 0 ? (
                  <span className="text-xl font-extrabold tabular-nums text-emerald-800">{sym}{money(winAmount)}</span>
                ) : (
                  <span className="text-sm font-bold text-emerald-700">In progress</span>
                )}
              </FinancialStatCard>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-line bg-subtle/20 p-4">
                <p className="text-xs font-bold text-heading mb-2">Battle details</p>
                <SummaryRow label="Type">{labelFor(BATTLE_TYPE_LABEL, battle.battle_type)}</SummaryRow>
                <SummaryRow label="Variant">{variantLabel}</SummaryRow>
                <SummaryRow label="Table">{battle.table_name || '—'}</SummaryRow>
                <SummaryRow label="Currency">{battle.currency_symbol} {battle.currency_code}</SummaryRow>
                <SummaryRow label="Players">{handPlayers.length > 0 ? handPlayers.length : '—'}</SummaryRow>
                <SummaryRow label="Betting rounds">
                  {battleDetails.rounds.length > 0 ? battleDetails.rounds.length : '—'}
                </SummaryRow>
              </div>

              <div className="rounded-xl border border-line bg-subtle/20 p-4">
                <p className="text-xs font-bold text-heading mb-2">Results &amp; timeline</p>
                <SummaryRow label="Created">{dateTime(battle.created_at)}</SummaryRow>
                <SummaryRow label="Ended">
                  {battle.ended_at ? dateTime(battle.ended_at) : <span className="text-emerald-700">In progress</span>}
                </SummaryRow>
                <SummaryRow label="Duration">
                  {formatDuration(battle.created_at, battle.ended_at) || (battle.ended_at ? '—' : <span className="text-emerald-700">Live</span>)}
                </SummaryRow>
                <SummaryRow label="Winner">
                  {battle.winner_user_id ? (
                    <button
                      type="button"
                      onClick={() => openPlayer(battle.winner_user_id)}
                      className="font-semibold text-heading hover:text-brand-gold hover:underline"
                    >
                      {battle.winner_display_name?.trim() || battle.winner_username || `USR-${battle.winner_user_id}`}
                    </button>
                  ) : (
                    battle.ended_at ? '—' : <span className="text-emerald-700">In progress</span>
                  )}
                </SummaryRow>
                <SummaryRow label="Private table">
                  {Number(battle.battle_type) === BATTLE_TYPE.PRIVATE && battle.private_table_id
                    ? `PT-${battle.private_table_id}`
                    : '—'}
                </SummaryRow>
                <SummaryRow label="Room code">
                  {Number(battle.battle_type) === BATTLE_TYPE.PRIVATE && battle.room_code ? (
                    <span className="font-mono">{battle.room_code}</span>
                  ) : (
                    '—'
                  )}
                </SummaryRow>
              </div>
            </div>

            <div className="px-6 pb-6">
              <UuidCopyRow uuid={battle.uuid} />
            </div>
          </div>

          {/* Players */}
          <div className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden">
            <div className="px-5 py-4 border-b border-line">
              <h4 className="font-bold text-heading">Seated Players &amp; Stacks</h4>
              <p className="text-xs text-muted mt-1">
                Per-hand chips: start minus bets, plus pot win (leave/cash-out is not used)
                {String(battle?.variant_code || '').toLowerCase() === 'ak47'
                  ? '. AK47: Dealt is the original hand; Ranked as is the joker combination used to decide the winner.'
                  : ''}
              </p>
            </div>
            <div className="overflow-auto">
              <table className={LIST_TABLE_CLASS}>
                <thead className={LIST_THEAD_CLASS}>
                  <tr>{['Seat', 'Player', 'User ID', 'Hand', 'Start Stack', 'End Stack', 'Net P/L', 'Result'].map((h, i) => (
                    <th key={i} className={LIST_TH_CLASS}>{h}</th>))}</tr>
                </thead>
                <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
                  {handPlayers.length > 0 ? handPlayers.map((p: any) => {
                    const net = parseFloat(p.net_amount ?? 0);
                    const isWinner = p.hand_result === 'winner';
                    return (
                    <tr key={p.user_id} className={`hover:bg-subtle/50 transition-colors ${isWinner ? 'bg-amber-500/[0.04]' : ''}`}>
                      <td className={`${LIST_TD_CLASS} font-bold text-heading whitespace-nowrap`}>{p.seat_no != null ? `#${p.seat_no}` : '—'}</td>
                      <td className={`${LIST_TD_CLASS} font-semibold text-heading whitespace-nowrap`}>
                        {isWinner && <Trophy size={14} className="inline mr-1.5 text-amber-600 -mt-0.5" />}
                        {playerLabel(p)}
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                        {p.user_id ? (
                          <button
                            type="button"
                            onClick={() => openPlayer(p.user_id)}
                            className="font-mono font-semibold text-heading hover:text-brand-gold hover:underline transition-colors"
                          >
                            {externalUserId(p)}
                          </button>
                        ) : (
                          <span className="font-mono text-muted">{externalUserId(p)}</span>
                        )}
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                        <AdminHandCards
                          cards={p.cards}
                          resolvedCards={p.resolved_cards}
                          handCategory={p.hand_category}
                          folded={Boolean(p.cards_folded)}
                          compact
                        />
                      </td>
                      <td className={`${LIST_TD_CLASS} text-muted whitespace-nowrap tabular-nums`}>{sym}{money(p.start_stack)}</td>
                      <td className={`${LIST_TD_CLASS} font-bold text-heading whitespace-nowrap tabular-nums`}>{sym}{money(p.end_stack)}</td>
                      <td className={`px-5 py-3 font-bold whitespace-nowrap tabular-nums ${net > 0 ? 'text-emerald-600' : net < 0 ? 'text-red-500' : 'text-muted'}`}>
                        {net > 0 ? '+' : ''}{sym}{money(net)}
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                        <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full border ${handResultPill(p.hand_result)}`}>
                          {handResultLabel(p.hand_result)}
                        </span>
                      </td>
                    </tr>
                    );
                  }) : <tr><td colSpan={8} className="px-5 py-8 text-center text-muted">No players recorded for this hand.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rounds */}
          <div className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden">
            <div className="px-5 py-4 border-b border-line">
              <h4 className="font-bold text-heading">Betting Rounds</h4>
              <p className="text-xs text-muted mt-1">Each round = one full turn cycle (all players act once)</p>
            </div>
            <div className="overflow-auto">
              <table className={LIST_TABLE_CLASS}>
                <thead className={LIST_THEAD_CLASS}>
                  <tr>{['Round', 'Status', 'Pot', 'Started', 'Ended', ''].map((h, i) => (
                    <th key={i} className={LIST_TH_CLASS}>{h}</th>))}</tr>
                </thead>
                <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
                  {battleDetails.rounds.length > 0 ? battleDetails.rounds.map((r: any) => (
                    <tr key={r.id} className="hover:bg-subtle/50 transition-colors">
                      <td className={`${LIST_TD_CLASS} font-bold text-heading whitespace-nowrap`}>R{r.round_number}</td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}><span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full border ${pill(ROUND_STATUS_LABEL, r.status)}`}>{labelFor(ROUND_STATUS_LABEL, r.status)}</span></td>
                      <td className={`${LIST_TD_CLASS} text-body whitespace-nowrap`}>{money(r.pot_amount)}</td>
                      <td className={`${LIST_TD_CLASS} text-muted text-xs whitespace-nowrap`}>{dateTime(r.started_at)}</td>
                      <td className={`${LIST_TD_CLASS} text-muted text-xs whitespace-nowrap`}>{r.ended_at ? dateTime(r.ended_at) : 'In progress'}</td>
                      <td className={`${LIST_TD_CLASS} text-right whitespace-nowrap`}>
                        <button
                          type="button"
                          onClick={() => openRound(battle.id, r)}
                          className="inline-flex items-center justify-center p-2 bg-indigo-500/10 hover:bg-indigo-500/15 text-indigo-700 border border-indigo-200/60 rounded-lg transition-all"
                          title="View round details"
                          aria-label="View round details"
                        >
                          <Eye size={14} strokeWidth={2} />
                        </button>
                      </td>
                    </tr>
                  )) : <tr><td colSpan={6} className="px-5 py-8 text-center text-muted">No rounds.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden">
            <div className="px-5 py-4 border-b border-line">
              <h4 className="font-bold text-heading">Action Log <span className="text-xs font-normal text-muted">(latest 50)</span></h4>
            </div>
            <div className="overflow-auto max-h-[62vh]">
              <table className={LIST_TABLE_CLASS}>
                <thead className={LIST_THEAD_CLASS}>
                  <tr>{['Round', 'Player', 'Action', 'Amount', 'Before', 'Remaining', 'Time'].map((h, i) => (
                    <th key={i} className={LIST_TH_CLASS}>{h}</th>))}</tr>
                </thead>
                <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
                  {battleDetails.actions.length > 0 ? battleDetails.actions.map((a: any) => (
                    <tr key={a.id} className="hover:bg-subtle/50 transition-colors">
                      <td className={`${LIST_TD_CLASS} text-muted whitespace-nowrap font-semibold`}>
                        {a.round_number != null ? `R${a.round_number}` : a.round_id ?? '—'}
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                        {a.user_id ? (
                          <button
                            type="button"
                            onClick={() => openPlayer(a.user_id)}
                            className="font-semibold text-heading hover:text-brand-gold hover:underline transition-colors"
                          >
                            {playerLabel(a)}
                          </button>
                        ) : (
                          <span className="text-body">{playerLabel(a)}</span>
                        )}
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}><span className="px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-500/10 text-indigo-700">{a.action_label ?? roundActionLabel(a.action_code, a.action)}</span></td>
                      <td className={`${LIST_TD_CLASS} font-semibold text-heading whitespace-nowrap tabular-nums`}>{sym}{money(a.amount)}</td>
                      <td className={`${LIST_TD_CLASS} text-muted whitespace-nowrap tabular-nums`}>
                        {a.balance_before != null ? `${sym}${money(a.balance_before)}` : '—'}
                      </td>
                      <td className={`${LIST_TD_CLASS} font-semibold text-heading whitespace-nowrap tabular-nums`}>
                        {a.balance_after != null ? `${sym}${money(a.balance_after)}` : '—'}
                      </td>
                      <td className={`${LIST_TD_CLASS} text-muted text-xs whitespace-nowrap`}>{dateTime(a.created_at)}</td>
                    </tr>
                  )) : <tr><td colSpan={7} className="px-5 py-8 text-center text-muted">No actions logged.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Round drill-down modal */}
      {selectedRound && (
        <div className="fixed inset-0 lg:left-72 bg-brand-dark/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-card w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden animate-scaleIn border border-line flex flex-col max-h-[90vh]">
            <div className="px-6 py-5 border-b border-indigo-800/30 flex items-center justify-between bg-gradient-to-r from-indigo-700 via-indigo-800 to-indigo-900 text-white shrink-0">
              <div>
                <h4 className="font-bold text-xl tracking-tight">Round R{selectedRound.round_number}</h4>
                <p className="text-sm text-indigo-200/90 mt-0.5">Actions &amp; money movements for this round</p>
              </div>
              <button
                onClick={closeRound}
                className="p-2 rounded-lg text-indigo-200 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {roundLoading ? (
              <div className="p-16 text-center text-muted font-semibold flex items-center justify-center gap-2">
                <RefreshCw className="animate-spin" /> Loading round…
              </div>
            ) : roundDetails ? (
              <div className="flex-1 overflow-y-auto">
                {/* Round summary */}
                <div className="p-6 border-b border-line bg-subtle/20">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-line bg-card p-4">
                      <p className="text-xxs font-bold text-muted uppercase tracking-wider mb-2">Status</p>
                      <span className={`px-2.5 py-1 inline-flex text-xs font-semibold rounded-full border ${pill(ROUND_STATUS_LABEL, roundDetails.round.status)}`}>
                        {labelFor(ROUND_STATUS_LABEL, roundDetails.round.status)}
                      </span>
                    </div>
                    <div className="rounded-xl border border-line bg-card p-4">
                      <p className="text-xxs font-bold text-muted uppercase tracking-wider mb-2">Pot</p>
                      <p className="text-lg font-bold text-heading tabular-nums">
                        {roundDetails.round.currency_symbol}{money(roundDetails.round.pot_amount)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-line bg-card p-4">
                      <p className="text-xxs font-bold text-muted uppercase tracking-wider mb-2">Ended</p>
                      <p className="text-sm font-semibold text-heading">
                        {roundDetails.round.ended_at ? dateTime(roundDetails.round.ended_at) : <span className="text-emerald-600">In progress</span>}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-8">
                  {/* Round actions */}
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-700">
                        <Activity size={16} />
                      </div>
                      <div>
                        <h5 className="text-sm font-bold text-heading">Player Actions</h5>
                        <p className="text-xs text-muted">Betting moves recorded in this round</p>
                      </div>
                    </div>

                    {(() => {
                      const seen = new Set<string>();
                      const roundPlayers: { key: string; label: string; userId?: number }[] = [];
                      for (const a of roundDetails.actions as any[]) {
                        const key = String(a.user_id);
                        if (seen.has(key)) continue;
                        seen.add(key);
                        roundPlayers.push({ key, label: playerLabel(a), userId: a.user_id });
                      }
                      return roundPlayers.length > 0 ? (
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                          <span className="text-xxs font-bold text-muted uppercase tracking-wide">
                            Players in round ({roundPlayers.length}):
                          </span>
                          {roundPlayers.map((p) => (
                            p.userId ? (
                              <button
                                key={p.key}
                                type="button"
                                onClick={() => openPlayer(p.userId!)}
                                className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-700 border border-indigo-200/60 hover:bg-indigo-500/20 transition-colors"
                              >
                                {p.label}
                              </button>
                            ) : (
                              <span key={p.key} className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-700 border border-indigo-200/60">
                                {p.label}
                              </span>
                            )
                          ))}
                        </div>
                      ) : null;
                    })()}

                    <div className="border border-line rounded-xl overflow-hidden shadow-sm">
                      <div className="overflow-auto max-h-56">
                        <table className={LIST_TABLE_CLASS}>
                          <thead className={LIST_THEAD_CLASS}>
                            <tr>{['Player', 'Action', 'Amount', 'Time'].map((h, i) => (
                              <th key={i} className="px-4 py-3 text-left text-xxs font-bold text-muted uppercase tracking-wider bg-subtle whitespace-nowrap">{h}</th>
                            ))}</tr>
                          </thead>
                          <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
                            {roundDetails.actions.length > 0 ? roundDetails.actions.map((a: any) => (
                              <tr key={a.id} className="hover:bg-subtle/40 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {a.user_id ? (
                                    <button
                                      type="button"
                                      onClick={() => openPlayer(a.user_id)}
                                      className="font-semibold text-heading hover:text-brand-gold hover:underline transition-colors"
                                    >
                                      {playerLabel(a)}
                                    </button>
                                  ) : (
                                    <span className="font-semibold text-heading">{playerLabel(a)}</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-500/10 text-indigo-700">
                                    {a.action_label ?? roundActionLabel(a.action_code, a.action)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 font-bold text-heading whitespace-nowrap tabular-nums">
                                  {roundDetails.round.currency_symbol}{money(a.amount)}
                                </td>
                                <td className="px-4 py-3 text-muted text-xs whitespace-nowrap">
                                  {new Date(a.created_at).toLocaleTimeString()}
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-muted text-sm">No actions logged.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </section>

                  {/* Round transactions (ledger) */}
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-700">
                        <Receipt size={16} />
                      </div>
                      <div>
                        <h5 className="text-sm font-bold text-heading">Player Transactions</h5>
                        <p className="text-xs text-muted">Immutable ledger entries for this round</p>
                      </div>
                    </div>

                    <div className="border border-line rounded-xl overflow-hidden shadow-sm">
                      <div className="overflow-auto max-h-64">
                        <table className={LIST_TABLE_CLASS}>
                          <thead className={LIST_THEAD_CLASS}>
                            <tr>{['Txn', 'Player', 'Type', 'Amount', 'Before', 'After', 'Time'].map((h, i) => (
                              <th key={i} className="px-4 py-3 text-left text-xxs font-bold text-muted uppercase tracking-wider bg-subtle whitespace-nowrap">{h}</th>
                            ))}</tr>
                          </thead>
                          <tbody className={`${LIST_TBODY_CLASS} text-sm`}>
                            {roundDetails.transactions.length > 0 ? roundDetails.transactions.map((g: any) => {
                              const amt = parseFloat(g.amount);
                              const cur = g.currency_symbol || roundDetails.round.currency_symbol || sym;
                              return (
                                <tr key={g.id} className="hover:bg-subtle/40 transition-colors">
                                  <td className="px-4 py-2.5 font-mono text-[11px] text-muted whitespace-nowrap" title={g.transaction_id}>
                                    {(g.transaction_id || '').slice(0, 8)}
                                  </td>
                                  <td className="px-4 py-2.5 whitespace-nowrap">
                                    {g.user_id ? (
                                      <button
                                        type="button"
                                        onClick={() => openPlayer(g.user_id)}
                                        className="font-semibold text-heading hover:text-brand-gold hover:underline transition-colors"
                                      >
                                        {playerLabel(g)}
                                      </button>
                                    ) : (
                                      <span className="font-semibold text-heading">{playerLabel(g)}</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2.5 whitespace-nowrap">
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-subtle text-body border border-line">
                                      {g.action_label ?? transactionTypeLabel(g.transaction_type, g.action_code)}
                                    </span>
                                  </td>
                                  <td className={`px-4 py-2.5 font-bold whitespace-nowrap tabular-nums ${amt >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                    {amt >= 0 ? '+' : '−'}{cur}{money(Math.abs(amt))}
                                  </td>
                                  <td className="px-4 py-2.5 text-muted whitespace-nowrap tabular-nums">{cur}{money(g.balance_before)}</td>
                                  <td className="px-4 py-2.5 font-semibold text-heading whitespace-nowrap tabular-nums">{cur}{money(g.balance_after)}</td>
                                  <td className="px-4 py-2.5 text-muted text-xs whitespace-nowrap">
                                    {new Date(g.created_at).toLocaleTimeString()}
                                  </td>
                                </tr>
                              );
                            }) : (
                              <tr>
                                <td colSpan={7} className="px-4 py-8 text-center text-muted text-sm">No transactions recorded for this round.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            ) : (
              <div className="p-16 text-center text-muted font-medium">Failed to load round details.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BattleDetail;
